import React, { useContext, useMemo } from 'react'
import { useNewTranslation, NewTrans } from 'hooks/index'
import { useSelector } from 'react-redux'
import { Text } from 'rebass'

import {
    REGISTRATION_VIEW_ENUM,
    selectFilterOptions,
    selectSearchTerm,
    selectSelectedCampaignSummary,
    selectSelectedRegistrationView,
    selectSelectedSubmissionView,
} from 'state/review'
import {
    SUBMISSION_VIEW_ENUM,
    selectBatchSubmissionsList,
    selectIsPendingSubmitSubmissionExist,
} from 'state/submissions'
import { selectCanSeeNextBatchStartModal, selectCanSeeNextBatchDisplaySoonModal } from 'state/profile'
import { theme } from 'theme'

import BaseEmptyState from 'components/Shared/EmptyState'
import { TableContext } from 'components/Shared/MuiTable/PartiTable'
import _ from 'lodash'

interface EmptyStateProps {
    my?: string
    title?: string
    body?: string
}

const EmptyState: React.FC<EmptyStateProps> = ({ my = theme.spacing.m, title, body }) => {
    const [newT] = useNewTranslation()
    const selectedSubmissionView = useSelector(selectSelectedSubmissionView)
    const selectedRegistrationView = useSelector(selectSelectedRegistrationView)
    const { showFilter } = useContext(TableContext)
    const filterOptions = useSelector(selectFilterOptions)
    const campaignSummary = useSelector(selectSelectedCampaignSummary)
    const batchSubmissionList = useSelector(selectBatchSubmissionsList)
    const canSeeNextBatchStartModal = useSelector(selectCanSeeNextBatchStartModal)
    const canSeeNextBatchDisplaySoonModal = useSelector(selectCanSeeNextBatchDisplaySoonModal)
    const isPendingSubmissionExist = useSelector(selectIsPendingSubmitSubmissionExist)
    const searchTerm = useSelector(selectSearchTerm)

    const showBatchNowDisplayNote =
        (batchSubmissionList.length <= 0 || isPendingSubmissionExist) &&
        canSeeNextBatchStartModal &&
        campaignSummary &&
        campaignSummary.batching_enable &&
        campaignSummary.total_submission_in_waiting_list != null &&
        campaignSummary.total_submission_in_waiting_list > 0
    const showBatchWillDisplayNote =
        (batchSubmissionList.length <= 0 || isPendingSubmissionExist) &&
        canSeeNextBatchDisplaySoonModal &&
        campaignSummary &&
        campaignSummary.batching_enable &&
        campaignSummary.total_submission_in_waiting_list != null &&
        campaignSummary.total_submission_in_waiting_list > 0

    const isFilterMode = useMemo(() => {
        return Boolean(showFilter) && Boolean(filterOptions && filterOptions?.length > 0)
    }, [showFilter, filterOptions?.length])

    return (
        <BaseEmptyState
            my={my}
            title={
                title
                    ? title
                    : isFilterMode || !_.isEmpty(searchTerm)
                      ? newT('emptyState.cantFindTitle')
                      : selectedRegistrationView === REGISTRATION_VIEW_ENUM.PENDING
                        ? newT('emptyState.noPendingRegistrationTitle')
                        : selectedRegistrationView === REGISTRATION_VIEW_ENUM.WAITLISTED
                          ? newT('emptyState.noRegistrationTitle')
                          : selectedRegistrationView === REGISTRATION_VIEW_ENUM.AWAITING_RESPONSE
                            ? newT('emptyState.noApprovedRegistrationTitle')
                            : selectedRegistrationView === REGISTRATION_VIEW_ENUM.ACCEPTED_INVITES
                              ? newT('emptyState.noAcceptedInvitesTitle')
                              : selectedRegistrationView === REGISTRATION_VIEW_ENUM.DECLINED_INVITES
                                ? newT('emptyState.noDeclinedInvitesTitle')
                                : selectedSubmissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS
                                  ? newT('emptyState.noPendingSubmissionTitle')
                                  : selectedSubmissionView === SUBMISSION_VIEW_ENUM.PENDING
                                    ? showBatchNowDisplayNote
                                        ? newT('emptyState.doneTitle')
                                        : newT('emptyState.noPendingSubmissionTitle', 'No pending submissions')
                                    : newT('emptyState.noSubmissionTitle', 'No submissions yet')
            }
            body={
                body ? (
                    body
                ) : isFilterMode ? (
                    newT('emptyState.changeFilterSubtitle')
                ) : !_.isEmpty(searchTerm) ? (
                    newT('emptyState.noSearchResultSubmissionSubtitle')
                ) : selectedRegistrationView === REGISTRATION_VIEW_ENUM.PENDING ? (
                    newT('emptyState.noRegistrationTitle')
                ) : selectedRegistrationView === REGISTRATION_VIEW_ENUM.WAITLISTED ? (
                    newT('emptyState.addPartipostersToWaitlistSubtitle')
                ) : selectedRegistrationView &&
                  [
                      REGISTRATION_VIEW_ENUM.AWAITING_RESPONSE,
                      REGISTRATION_VIEW_ENUM.ACCEPTED_INVITES,
                      REGISTRATION_VIEW_ENUM.DECLINED_INVITES,
                  ].includes(selectedRegistrationView) ? (
                    ''
                ) : selectedSubmissionView === SUBMISSION_VIEW_ENUM.BATCH_SUBMISSIONS ? (
                    newT('emptyState.noSubmissionForTaskSubtitle', 'There are no submissions for this task just yet.')
                ) : selectedSubmissionView === SUBMISSION_VIEW_ENUM.PENDING ? (
                    showBatchNowDisplayNote ? (
                        newT('emptyState.clearedPendingSubmissionSubtitle')
                    ) : showBatchWillDisplayNote ? (
                        <NewTrans i18nKey="emptyState.waitlistOnceForReviewSubmissionAcceptedSubtitle">
                            <Text display="inline" fontSize="12px">
                                You will be able to see the waiting list once an account manager has accepted all the
                                submissions in the{' '}
                            </Text>
                            <Text display="inline" fontSize="12px" fontWeight="bold">
                                For Review
                            </Text>
                            <Text display="inline" fontSize="12px">
                                {' '}
                                page.
                            </Text>
                        </NewTrans>
                    ) : (
                        newT(
                            'emptyState.noSubmissionForTaskSubtitle',
                            'There are no submissions for this task just yet.',
                        )
                    )
                ) : selectedSubmissionView === SUBMISSION_VIEW_ENUM.COMPLETED ? (
                    newT(
                        'emptyState.noApprovedSubmissionSubtitle',
                        'There are no approved submissions yet. Approve some Partiposters?',
                    )
                ) : selectedSubmissionView === SUBMISSION_VIEW_ENUM.EDITS_REQUESTED ? (
                    newT(
                        'emptyState.noAwaitingEditsSubtitle',
                        'There are no submissions awaiting edits from Partiposters.',
                    )
                ) : selectedSubmissionView === SUBMISSION_VIEW_ENUM.DROPOUTS ? (
                    newT('emptyState.noDropoutsSubtitle', 'No Partiposters have dropped out of this campaign!')
                ) : selectedSubmissionView === SUBMISSION_VIEW_ENUM.REJECTED ? (
                    newT('emptyState.noRejectedSubmissionSubtitle', 'You have not rejected any Partiposters!')
                ) : (
                    newT('emptyState.noForReviewSubmissionSubtitle', 'There are no submissions for you to review!')
                )
            }
            backBtnType={
                isFilterMode
                    ? undefined
                    : selectedRegistrationView !== REGISTRATION_VIEW_ENUM.WAITLISTED && _.isEmpty(searchTerm)
                      ? showBatchWillDisplayNote
                          ? 'AllSubmissions'
                          : 'PartipostsDashboard'
                      : undefined
            }
        />
    )
}

export default EmptyState
